import _debounce from "lodash/debounce";
import _fromPairs from "lodash/fromPairs";
import _throttle from "lodash/throttle";
import { useCallback, useEffect, useMemo, useState } from "react";

import { STANDARD_BREAKPOINTS } from "lib/constants";

/**
 * Listens for resize events and updates a state variable with current width/height
 *
 * @returns state object with current width and height in pixels
 */
export const useWindowSize = ({ debounceMS = 100, handleResize } = {}) => {
    const [windowSize, setWindowSize] = useState({
        width: window?.innerWidth,
        height: window?.innerHeight,
    });

    useEffect(() => {
        const onResize = _debounce(() => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
            if (handleResize) {
                handleResize();
            }
        }, debounceMS);

        window.addEventListener("resize", onResize);

        // set initial size
        onResize();

        // effect cleanup
        return () => {
            onResize.cancel();
            window.removeEventListener("resize", onResize);
        };
    }, []);

    return windowSize;
};

function getBreakpointWidth(breakpoints, width) {
    const lesser = breakpoints.filter((b) => b < width);
    return lesser.length > 0 ? Math.max(...lesser) : null;
}

/**
 * Given an array of integer widths return the width we are above and closest to
 *
 * If breakpoints is [720, 1440] and width is currently 1280 then return 720
 * If width is greater than all breakpoints then return null
 *
 * The reason it's done this way is because the breakpoints are really ranges of values.
 * For instance, 0-599 is "xs", 600-899 is "sm", etc, so we want to appropriately attach
 * the value of the "bucket" we're inside.
 */
export const useWindowWidthBreakpoints = (breakpoints) => {
    const sortedBreakpoints = breakpoints.slice().sort((a, b) => a - b);
    const { width } = useWindowSize();
    const [breakpoint, setBreak] = useState(getBreakpointWidth(sortedBreakpoints, width));

    useEffect(() => {
        const current = getBreakpointWidth(sortedBreakpoints, width);
        if (current !== breakpoint) {
            setBreak(current);
        }
    }, [width]);

    return breakpoint;
};

/**
 * Use our standard breakpoints
 */
export const useBreakpoints = () => useWindowWidthBreakpoints(Object.values(STANDARD_BREAKPOINTS));

/**
 * Returns an object that lets you pick which breakpoints you care about!
 */
export const useIsBreakpoint = () => {
    const breakpoint = useBreakpoints();
    return {
        breakpointXS: breakpoint === STANDARD_BREAKPOINTS.xs,
        breakpointSM: breakpoint === STANDARD_BREAKPOINTS.sm,
        breakpointMD: breakpoint === STANDARD_BREAKPOINTS.md,
        breakpointLG: breakpoint === STANDARD_BREAKPOINTS.lg,
        breakpointXL: breakpoint === STANDARD_BREAKPOINTS.xl,
    };
};

/**
 * Maybe deprecated for the above function?
 */
export const useIsBreakpointXS = () => {
    const { breakpointXS } = useIsBreakpoint();
    return breakpointXS;
};

/**
 * Returns a value for a specific breakpoint.
 *
 * Options can be an array or object:
 * [[breakpoint, value], [breakpoint, value], [breakpoint, value]]
 * {breakpoint: value, breakpoint: value, breakpoint: value}
 *
 * Returns the specified value or null if no matching breakpoint exists.
 */
export const useValueByBreakpoints = (
    options,
    breakpoints = Object.values(STANDARD_BREAKPOINTS),
) => {
    const opts = useMemo(() => (Array.isArray(options) ? _fromPairs(options) : options), [options]);

    const breakpoint = useWindowWidthBreakpoints(breakpoints);

    return opts[breakpoint] === undefined ? null : opts[breakpoint];
};

/**
 * MUI has some very undesired "bouncing" for popups and dialogs if a user has
 * their OS set to always show scrollbars. This will allow you to detect when
 * a permanent scrollbar exists because the document's scrollable width will
 * be less than the window's width.
 */
export const useHasPermanentScrollbar = () => {
    const [docWidth, setDocWidth] = useState(window?.document?.body?.scrollWidth);

    const { width } = useWindowSize({
        handleResize: () => {
            setDocWidth(window.document.body.scrollWidth);
        },
    });

    return width - docWidth > 0;
};

const scrollListeners = [];
let scrollAttached = false;

const startWindowScroll = () => {
    if (scrollAttached) {
        return;
    }

    function onScroll() {
        scrollListeners.forEach((listener) => listener());
    }

    scrollAttached = true;

    const _onScroll = _throttle(onScroll, 100); // eslint-disable-line no-underscore-dangle
    document.addEventListener("scroll", _onScroll);
};

/**
 * Requires that window/document is the scrolling container
 */
export const useIsInViewport = (elRef, jitterPX = 200) => {
    const container = document.querySelector("html");
    const [isInViewport, setIsInViewport] = useState(false);

    function onScroll() {
        const el = elRef.current;
        if (!container || !el) {
            return;
        }

        const bottom = container.offsetTop + container.clientHeight;
        const bounds = el.getBoundingClientRect();

        setIsInViewport(bounds.top >= -jitterPX && bounds.top < bottom + jitterPX);
    }

    useEffect(() => {
        onScroll(); // Get an initial value

        startWindowScroll();
        scrollListeners.push(onScroll);

        return () => scrollListeners.splice(scrollListeners.indexOf(onScroll), 1);
    }, [container]);

    return isInViewport;
};

/**
 * Returns window.scrollY when the "scroll" event is triggered.
 */
export const useScrollY = () => {
    const [scrollY, setScrollY] = useState(window.scrollY);

    const handleScroll = useCallback(() => {
        setScrollY(window.scrollY);
    }, []);

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    return scrollY;
};

const minScrollBuffer = 8; // if scrolled less than this amount, unlatch
let scrollBuffer = minScrollBuffer; // poor man's context, just a module-level var
export const useScrollLatch = (shrinkSize) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const scrollY = useScrollY();
    useEffect(() => {
        // allow the scroll buffer to account for as many scroll latch users as needed
        scrollBuffer += shrinkSize;
        return () => (scrollBuffer -= shrinkSize); // reduce if those users disappear
    }, []);

    useEffect(() => {
        if (scrollY >= scrollBuffer) setIsScrolled(true);
        if (scrollY < minScrollBuffer) setIsScrolled(false);
    }, [scrollY]);

    return { isScrolled, scrollY, scrollBuffer };
};
